import { useEffect, useState } from 'react';
import { messaging, getToken, onMessage, VAPID_KEY } from '../firebase';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const isNative = Capacitor.isNativePlatform();
const notificationsSupported = typeof Notification !== 'undefined';

export default function useNotifications() {
  const [permission, setPermission] = useState(
    isNative ? 'default' : (notificationsSupported ? Notification.permission : 'denied')
  );
  const [token, setToken]           = useState(null);
  const [notification, setNotification] = useState(null);

  const requestPermission = async () => {
    // ── Native app (iOS / Android) ──
    if (isNative) {
      const { receive } = await PushNotifications.requestPermissions();
      if (receive === 'granted') {
        setPermission('granted');
        await PushNotifications.register();
      } else {
        setPermission('denied');
      }
      return;
    }

    // ── Web browser ──
    if (!notificationsSupported) return;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm === 'granted' && messaging) {
        const fcmToken = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (fcmToken) setToken(fcmToken);
      }
    } catch (err) {
      console.error('Notification permission error:', err);
    }
  };

  useEffect(() => {
    if (isNative) {
      // Get FCM token when registration succeeds
      PushNotifications.addListener('registration', (t) => {
        setToken(t.value);
        setPermission('granted');
        console.log('FCM Token (native):', t.value);
      });

      // Handle notification received while app is open
      PushNotifications.addListener('pushNotificationReceived', (n) => {
        setNotification(n);
      });

      // Handle tap on notification
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('Notification tapped:', action.notification);
      });

      return () => PushNotifications.removeAllListeners();
    }

    // Web: listen for foreground messages
    if (!messaging || !notificationsSupported) return;
    const unsub = onMessage(messaging, (payload) => {
      setNotification(payload.notification);
      if (Notification.permission === 'granted') {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/logo192.png',
        });
      }
    });
    return () => unsub();
  }, []);

  return { token, permission, notification, requestPermission };
}
